/**
 * Les statistiques de la plateforme (docs/16 §8).
 *
 * Deux blocs qui répondent à deux questions différentes : **où en est-on**
 * (l'instantané) et **d'où vient-on** (les courbes). Un écran qui n'aurait que
 * le premier ne dirait pas si 1 240 comptes est beaucoup ; un écran qui n'aurait
 * que le second ne dirait pas ce que valent les vingt inscriptions d'hier.
 *
 * **Aucun taux n'est calculé côté serveur**, et celui-ci n'en invente pas non
 * plus au-delà de ce qu'il affiche : les nombres bruts sont montrés tels quels,
 * et les seules divisions faites ici — le MRR en euros, les octets en Go — sont
 * des conversions d'unité, pas des indicateurs.
 *
 * Le nombre d'abonnements **sans tarif** est affiché à côté du MRR dès qu'il
 * n'est pas nul. C'est la seule façon honnête de servir un revenu que
 * `ManualProvider` peut sous-estimer : le taire donnerait un chiffre qui a l'air
 * juste.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGrowth, usePlatformStats } from "@/features/admin/api/useAdmin";
import { SparkBars } from "@/features/admin/components/SparkBars";
import { Alert } from "@/shared/ui/Alert";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

const WINDOWS = [7, 30, 90] as const;

export function AdminStatsPage() {
  const { t, i18n } = useTranslation("admin");
  const [days, setDays] = useState<number>(30);
  const snapshot = usePlatformStats();
  const growth = useGrowth(days);

  const number = new Intl.NumberFormat(i18n.language);
  const formatDay = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(value));

  if (snapshot.isPending) {
    return <Skeleton className="my-8 h-64 w-full" label={t("stats.title")} />;
  }
  if (snapshot.isError) {
    return <ErrorState error={snapshot.error} />;
  }

  const data = snapshot.data;
  const accounts = Object.values(data.accounts_by_status).reduce((sum, n) => sum + n, 0);
  const enrollments = Object.values(data.enrollments_by_status).reduce((sum, n) => sum + n, 0);
  const subscribers = Object.values(data.subscriptions_by_plan).reduce((sum, n) => sum + n, 0);

  const series = [
    { key: "signups", points: growth.data?.signups ?? [] },
    { key: "coursesPublished", points: growth.data?.courses_published ?? [] },
    { key: "enrollments", points: growth.data?.enrollments ?? [] },
    { key: "completions", points: growth.data?.completions ?? [] },
  ] as const;

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("stats.title")}</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">{t("stats.subtitle")}</p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={t("stats.accounts")} value={number.format(accounts)}>
          {t("stats.accountsDetail", {
            active: number.format(data.accounts_by_status["active"] ?? 0),
            suspended: number.format(data.accounts_by_status["suspended"] ?? 0),
          })}
        </StatTile>
        <StatTile
          label={t("stats.courses")}
          value={number.format(data.courses_by_status["published"] ?? 0)}
        >
          {t("stats.coursesDetail", {
            drafts: number.format(data.courses_by_status["draft"] ?? 0),
          })}
        </StatTile>
        <StatTile label={t("stats.enrollments")} value={number.format(enrollments)}>
          {t("stats.enrollmentsDetail", {
            completed: number.format(data.enrollments_by_status["completed"] ?? 0),
          })}
        </StatTile>
        <StatTile label={t("stats.certificates")} value={number.format(data.certificates_issued)}>
          {t("stats.certificatesDetail", {
            revoked: number.format(data.certificates_revoked),
          })}
        </StatTile>
        <StatTile
          label={t("stats.mrr")}
          value={new Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: data.currency,
            maximumFractionDigits: 0,
            // Les montants restent en centimes jusqu'à l'affichage (docs/22 §4).
          }).format(data.mrr_cents / 100)}
        >
          {t("stats.mrrDetail", { subscribers: number.format(subscribers) })}
        </StatTile>
        <StatTile
          label={t("stats.storage")}
          value={t("stats.gigabytes", {
            value: (data.stored_bytes / 1024 ** 3).toFixed(2),
          })}
        >
          {t("stats.storageDetail", { files: number.format(data.stored_files) })}
        </StatTile>
      </section>

      {data.unpriced_subscriptions > 0 && (
        // Affiché **seulement** quand le cas existe, et jamais masqué quand il
        // existe : un MRR qui tairait des abonnements sans tarif sous-estimerait
        // sans le dire, ce qui est pire qu'un chiffre manquant.
        <Alert tone="warning">{t("stats.unpriced", { count: data.unpriced_subscriptions })}</Alert>
      )}

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{t("stats.growth")}</h2>
          <div className="flex gap-2" role="group" aria-label={t("stats.window")}>
            {WINDOWS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setDays(option);
                }}
                aria-pressed={days === option}
                className={
                  days === option
                    ? "bg-brand-600 rounded-md px-3 py-1 text-sm text-white"
                    : "rounded-md border border-slate-300 px-3 py-1 text-sm dark:border-slate-700"
                }
              >
                {t("stats.days", { count: option })}
              </button>
            ))}
          </div>
        </div>

        {growth.isError ? (
          <ErrorState error={growth.error} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {series.map(({ key, points }) => (
              <Card key={key} title={t(`stats.series.${key}`)}>
                {growth.isPending ? (
                  <Skeleton className="h-12 w-full" label={t(`stats.series.${key}`)} />
                ) : (
                  <div className="space-y-2">
                    <p className="text-2xl font-semibold">
                      {number.format(points.reduce((sum, point) => sum + point.count, 0))}
                    </p>
                    <SparkBars
                      points={points}
                      label={t("stats.chartLabel", {
                        series: t(`stats.series.${key}`),
                        days,
                      })}
                      describe={(point) =>
                        t("stats.point", {
                          day: formatDay(point.day),
                          count: number.format(point.count),
                        })
                      }
                    />
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

interface TileProps {
  label: string;
  value: string;
  children: React.ReactNode;
}

function StatTile({ label, value, children }: TileProps) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs tracking-wide text-slate-500 uppercase dark:text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{children}</p>
    </div>
  );
}
